import searchIcon from "@loot-os/icons/icons/search";
import { ChipRadioGroup } from "../../../../core/Chip.js";
import { EmptyState } from "../../../../core/EmptyState.js";
import { Input } from "../../../../core/Input.js";
import { LoadMore } from "../../../../core/LoadMore.js";
import { Panel } from "../../../../core/Panel.js";
import { Skeleton } from "../../../../core/Skeleton.js";
import { Text } from "../../../../core/Text.js";
import { ItemCard } from "../../../../domain/ItemCard.js";
import { LoadError } from "../../../shared/LoadError.js";
import type { ForgeItemGroup, ForgePickerContent } from "../../forgeContent.js";

const GRID = "grid grid-cols-2 gap-2 sm:grid-cols-3 md:gap-3 xl:grid-cols-4";

/**
 * Step 3: search the catalog by name and value, filter to the Mystery Bulk Cards, and pick items
 * from a grid of toggles (13 · Exchange, Pick trade-ins). The list scrolls inside the step.
 */
export function ForgeItemPicker({
  step,
  title,
  count,
  currency,
  search,
  min,
  max,
  group,
  rangeError,
  error,
  state,
  more,
}: ForgePickerContent) {
  return (
    <Panel size="sm" title={title} eyebrow={step} description={count}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_8rem_8rem]">
        <Input
          type="search"
          label={search.label}
          hideLabel
          icon={searchIcon}
          placeholder={search.placeholder}
          value={search.value}
          onChange={(event) => search.onChange(event.target.value)}
          className="col-span-2 sm:col-span-1"
        />
        {[min, max].map((field) => (
          <Input
            key={field.label}
            label={field.label}
            hideLabel
            mono
            inputMode="decimal"
            placeholder={field.placeholder}
            value={field.value}
            invalid={field.invalid}
            onChange={(event) => field.onChange(event.target.value)}
          />
        ))}
      </div>
      <ChipRadioGroup
        label={group.label}
        options={group.options}
        value={group.value}
        onValueChange={(next: ForgeItemGroup) => group.onValueChange(next)}
      />
      {rangeError && (
        <Text as="p" variant="body-b3" tone="danger">
          {rangeError}
        </Text>
      )}
      {error && <LoadError message={error} />}
      <div className="max-h-[36rem] overflow-y-auto pr-1 max-md:max-h-[28rem]">
        {state.status === "loading" && (
          <div aria-busy className={GRID}>
            {["a", "b", "c", "d", "e", "f"].map((key) => (
              <Skeleton key={key} className="aspect-[4/5] rounded-xl" />
            ))}
          </div>
        )}
        {state.status === "ready" && (
          <div className="flex flex-col gap-4">
            {state.groups.map((itemGroup) => (
              <section key={itemGroup.title ?? "items"} className="flex flex-col gap-2">
                {itemGroup.title && (
                  <Text as="h3" variant="caption-eyebrow" tone="tertiary">
                    {itemGroup.title}
                  </Text>
                )}
                <ul className={GRID}>
                  {itemGroup.items.map((item) => (
                    <li key={item.id} className="flex">
                      <ItemCard
                        variant="owned"
                        name={item.name}
                        image={item.image}
                        note={{ kind: "value", cents: item.value }}
                        selection={{ selected: item.selected, onToggle: item.onToggle }}
                        currency={currency}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            {state.empty && <EmptyState icon={searchIcon} title={state.empty} />}
            {more && (
              <LoadMore
                shown={more.shown}
                hasMore
                autoLoad
                loading={more.loading}
                label={more.label}
                onLoadMore={more.onLoadMore}
              />
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
